import { SuspenseBoundary } from "@/components/SuspenseBoundary";
import { LOGO_DATA } from "@/constants/ui";
import { useAuth } from "@/hooks/auth/useAuth";
import { useUserRoutes } from "@/hooks/auth/useUserRoutes";
import { changeAppLanguage } from "@/i18n";
import { DownOutlined, GlobalOutlined, LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Menu, Space } from "antd";
import type { MenuProps } from "antd";
import { Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const HEADER_HEIGHT = 48
const MAIN_PAGE_URL = '/tracking'

interface LogoProps {
    onClick: () => void
}

interface MenuItem {
    label: string
    key: string
    path: string
    sort?: number
}

const Logo = ({ onClick }: LogoProps) => {
    const logoImage = LOGO_DATA || ""
    return (
        <div
            className="h-2 m-4 rounded-md text-center flex justify-center items-center cursor-pointer "
            style={{
                background: 'rgba(255,255,255,.2)',
            }}
            onClick={onClick}
        >
            {logoImage && (
                <img
                    src={logoImage}
                    className='max-w-[300px] h-9 transition-all	duration-200'
                />)}
        </div>
    )
}


export const MainLayout = () => {
    const router = useRouter()
    const currentPath = useRouterState({ select: (state) => state.location.pathname })
    const { t, i18n } = useTranslation()
    const { logout, userInfo } = useAuth()
    const { routers } = useUserRoutes()
    const currentLanguage = i18n.resolvedLanguage || i18n.language
    const isEnglishMode = currentLanguage === 'en'
    const isEnglishRouteAllowed = useRouterState({
        select: (state) => state.matches.some((m) => Boolean(m.staticData?.englishAllowed)),
    })
    const englishMenuItems = useMemo<MenuItem[]>(() => [
        {
            label: t('shell.vesselTracking'),
            key: 'vessel-tracking',
            path: MAIN_PAGE_URL,
        }
    ], [t])

    const items = useMemo(() => {
        if (isEnglishMode) return englishMenuItems

        return routers.map((router) => {
            return {
                label: router.router_name,
                key: router.router_name,
                path: router.router_value,
                sort: router.router_sort
            }
        }).sort((a, b) => (b.sort ?? 0) - (a.sort ?? 0))
    }, [routers, isEnglishMode, englishMenuItems])

    const handleMenuClick: MenuProps['onClick'] = (event) => {
        const { key } = event
        const target = items.find(menuItem => menuItem.key == key)
        if (target) {
            router.history.push(target.path)
        }
    }


    const selectedKey = useMemo(() => {
        if (isEnglishMode) {
            return currentPath.startsWith('/tracking') ? 'vessel-tracking' : undefined
        }
        const firstPath = currentPath.split('/')[1]
        return items.find(item => item.path.includes(firstPath))?.key
    }, [currentPath, items, isEnglishMode])


    const handleLogoClick = () => {
    }

    const handleProfileMenuClick: MenuProps['onClick'] = async ({ key }) => {
        if (key === 'logout') {
            logout()
        }
    }

    const profileMenuItems = useMemo(() => {
        return [
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: t('shell.signOut'),
            },
        ]
    }, [t])

    const profileLabel = userInfo?.nickname || userInfo?.username || ''

    const handleLanguageToggle = async () => {
        const nextLanguage = isEnglishMode ? 'zh-CN' : 'en'
        await changeAppLanguage(nextLanguage)

        if (nextLanguage === 'en' && !isEnglishRouteAllowed && currentPath !== MAIN_PAGE_URL) {
            router.history.replace(MAIN_PAGE_URL)
        }
    }

    const languageToggle = (
        <Button
            type="text"
            onClick={handleLanguageToggle}
            className="flex h-8 items-center gap-1 rounded-md px-2 text-slate-600 hover:!bg-slate-100 hover:!text-slate-800"
        >
            <GlobalOutlined className="text-sm" />
            <span className="text-sm">{isEnglishMode ? '中文' : 'EN'}</span>
        </Button>
    )

    const profileTrigger = (
        <Button
            type="text"
            title={t('shell.accountMenu')}
            className="mr-4 flex h-10 max-w-[220px] items-center rounded-full px-2 text-slate-700 hover:!bg-slate-50"
        >
            <Space size={8} className="min-w-0">
                <Avatar size="small" icon={<UserOutlined />} />
                <span className="max-w-[132px] truncate text-sm font-medium text-slate-700">{profileLabel}</span>
                <DownOutlined className="text-xs text-slate-400" />
            </Space>
        </Button>
    )

    const profileDropdown = (
        <Dropdown
            trigger={['hover']}
            placement="bottomRight"
            menu={{
                items: profileMenuItems,
                onClick: handleProfileMenuClick,
            }}
        >
            {profileTrigger}
        </Dropdown>
    )

    return <div className="min-h-screen bg-[#f5f5f5]">
        <div
            className='fixed top-0 left-0 right-0 z-[1000] flex items-center justify-between bg-white shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)]'
            style={{ height: HEADER_HEIGHT }}
        >
            <Logo onClick={handleLogoClick} />
            <div className='flex min-w-0 items-center gap-2'>
                <Menu key={currentLanguage} className="max-w-[500px]" selectedKeys={selectedKey ? [selectedKey] : []} theme="light" mode="horizontal" items={items} onClick={handleMenuClick} />
                {languageToggle}
                {profileDropdown}
            </div>
        </div>

        <div className="bg-[#f5f5f5]" style={{ marginTop: HEADER_HEIGHT }}>
            <SuspenseBoundary>
                <Outlet />
            </SuspenseBoundary>
        </div>
    </div>
};
